import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import { Button, Divider, Dropdown, Image, Input, Message, Modal, Segment } from 'semantic-ui-react';

import { extractAssetURLData } from '../../utils/asset-url-helper-service';
import { scientificFormat } from '../../utils/helpers/formatting';
import { InstagramIcon, TiktokIcon } from '../../components/dyzio-icons';
import { INSTAGRAM, TIKTOK } from '../../utils/static-configuration';

const DEFAULT_IMAGE = 'https://s3-eu-west-1.amazonaws.com/discussions-uploads-production/no_image_available.png';
const MIN_SEARCH = 2;
const SEARCH_DEBOUNCE_MS = 300;
const PICKER_LIMIT = 25;

const noticeStyle = { marginTop: 12 };
const stepStyle = { marginBottom: 4 };
const stepLabelStyle = { fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center' };
const detectedStyle = { marginTop: 8, color: '#9090a0' };
const matchBadgeStyle = {
  marginLeft: 8,
  fontWeight: 400,
  fontSize: '0.85em',
  color: '#21ba45',
  border: '1px solid #21ba45',
  borderRadius: 3,
  padding: '1px 6px',
};
const mutedStyle = { color: '#9090a0' };
const cardStyle = { display: 'flex', alignItems: 'center', marginTop: 12 };
const cardBodyStyle = { minWidth: 0, lineHeight: 1.45 };
const avatarStyle = { marginRight: 14, flexShrink: 0 };
const nameStyle = { fontWeight: 600 };
const iconBoxStyle = { width: 18, display: 'inline-flex', justifyContent: 'flex-start', flexShrink: 0 };
const platformRowStyle = { display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' };
const countStyle = { color: '#9090a0', marginLeft: 6 };

const PLATFORM_LABEL = { tiktok: 'TikTok video', instagram: 'Instagram post' };
const PLATFORM_CONST = { tiktok: TIKTOK, instagram: INSTAGRAM };

const handleField = platform => (platform === TIKTOK ? 'tiktok' : 'instagram');
const norm = value => _.toLower(_.trim(value || '')).replace(/^@/, '');

// Semantic filters options client-side by default, which would hide results the API matched.
const keepApiOrder = options => options;

const optionFor = creator => {
  const influencer = creator.influencer || {};
  const handle = influencer.primarySocialHandle || creator.name;
  return {
    key: creator.id,
    value: creator.id,
    text: handle,
    description: influencer.name && influencer.name !== handle ? influencer.name : undefined,
    image: { avatar: true, src: influencer.thumbnail || DEFAULT_IMAGE },
    creator,
  };
};

// Once a creator is chosen, show enough to confirm it is the right one before attaching.
const CreatorCard = ({ creator }) => {
  const influencer = creator.influencer || {};
  const handle = influencer.primarySocialHandle || creator.name;
  const scanned = creator.lastScannedDate
    ? `Last scanned ${moment(creator.lastScannedDate).fromNow()}`
    : 'Never scanned';

  return (
    <Segment style={cardStyle}>
      <Image avatar size="mini" src={influencer.thumbnail || DEFAULT_IMAGE} style={avatarStyle} />
      <div style={cardBodyStyle}>
        <div style={nameStyle}>{influencer.name || handle}</div>
        {influencer.name && influencer.name !== handle &&
        <div style={mutedStyle}>@{handle}</div>}
        {influencer.instagram &&
        <div style={platformRowStyle}>
          <span style={iconBoxStyle}><InstagramIcon /></span>
          <span>@{influencer.instagram}</span>
          <span style={countStyle}>{scientificFormat(influencer.instagramCount || 0)}</span>
        </div>}
        {influencer.tiktok &&
        <div style={platformRowStyle}>
          <span style={iconBoxStyle}><TiktokIcon /></span>
          <span>@{influencer.tiktok}</span>
          <span style={countStyle}>{scientificFormat(influencer.tiktokCount || 0)}</span>
        </div>}
        <div style={mutedStyle}>{scanned}</div>
      </div>
    </Segment>
  );
};

CreatorCard.propTypes = { creator: PropTypes.object.isRequired };

// One self-contained modal: paste a URL, confirm which collaboration it belongs to, attach it.
// Deliberately does not reuse AddAssetForm - that renders its own modal, and React 15 has no
// portals, so swapping one modal for another mid-flow leaves the overlay in a broken state.
class AddJumboContentModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      url: '',
      details: null,
      urlError: null,
      options: [],
      selectedId: null,
      matchedId: null,
      isSearching: false,
      isSaving: false,
      error: null,
    };
  }

  componentWillUnmount() {
    this.debouncedSearch.cancel();
  }

  onUrlChange = (e, { value }) => {
    const details = extractAssetURLData(_.trim(value));
    const expected = PLATFORM_CONST[this.props.platform];

    if (!_.trim(value)) {
      this.setState({ url: value, details: null, urlError: null, options: [], selectedId: null, matchedId: null });
      return;
    }
    if (!details) {
      this.setState({ url: value, details: null, urlError: 'That does not look like a post URL.' });
      return;
    }
    if (details.platform !== expected) {
      this.setState({
        url: value,
        details: null,
        urlError: `That is a ${_.toLower(details.platform)} URL - `
          + `this dialog adds a ${PLATFORM_LABEL[this.props.platform]}.`,
      });
      return;
    }
    this.setState({ url: value, details, urlError: null, error: null }, () => this.suggestCreator(details));
  };

  onPickerSearch = (e, { searchQuery }) => {
    if (_.size(_.trim(searchQuery)) < MIN_SEARCH) return;
    this.debouncedSearch(searchQuery);
  };

  onPickerChange = (e, { value }) => this.setState({ selectedId: value });

  onConfirm = async () => {
    const { options, selectedId, details } = this.state;
    const creator = _.get(_.find(options, { value: selectedId }), 'creator');
    if (!creator || !details) return;

    this.setState({ isSaving: true, error: null });
    try {
      await this.props.onAttach({ creator, details });
      this.setState({ isSaving: false });
      this.props.onAttached(creator, details);
    } catch (err) {
      this.setState({ isSaving: false, error: _.get(err, 'message') || 'Could not add the content.' });
    }
  };

  // TikTok URLs contain the handle so we can pre-select a match. Instagram post URLs carry only
  // the shortcode, so there is nothing to match on and the user picks from the full list.
  suggestCreator = async details => {
    const handle = norm(details.handle);
    this.setState({ isSearching: true });
    try {
      const creators = await this.fetchCreators(handle || undefined);
      const field = handleField(details.platform);
      const exact = handle
        ? _.find(creators, c => norm(_.get(c, `influencer.${field}`)) === handle)
        : null;
      this.setState({
        isSearching: false,
        options: _.map(creators, optionFor),
        selectedId: exact ? exact.id : null,
        matchedId: exact ? exact.id : null,
      });
    } catch (err) {
      this.setState({ isSearching: false, error: 'Could not load creators. Use the search box.' });
    }
  };

  searchCreators = async term => {
    this.setState({ isSearching: true });
    try {
      const creators = await this.fetchCreators(term);
      const selected = _.find(this.state.options, { value: this.state.selectedId });
      const merged = _.uniqBy(_.compact([selected, ..._.map(creators, optionFor)]), 'value');
      this.setState({ isSearching: false, options: merged });
    } catch (err) {
      this.setState({ isSearching: false, error: 'Could not search creators. Please try again.' });
    }
  };

  debouncedSearch = _.debounce(term => this.searchCreators(term), SEARCH_DEBOUNCE_MS);

  fetchCreators = async term => {
    const response = await this.props.campaignAPIClient.get(
      `/jumbo/campaigns/${this.props.campaignId}/creators`,
      { params: { search: term, perPage: PICKER_LIMIT } },
    );
    return _.get(response, 'data.data.data', []);
  };

  renderNotice() {
    const { details, options, selectedId, matchedId } = this.state;
    if (!details) return null;

    const chosen = _.get(_.find(options, { value: selectedId }), 'creator');
    const field = handleField(details.platform);
    const existing = chosen ? _.get(chosen, `influencer.${field}`) : null;

    if (existing && details.handle && norm(existing) !== norm(details.handle)) {
      return (
        <Message
          negative
          style={noticeStyle}
          header="This will replace an existing handle"
          content={`${_.get(chosen, 'influencer.name') || chosen.name} currently has `
            + `${field} @${existing}. Saving will change it to @${details.handle}.`}
        />
      );
    }
    if (chosen && !existing && details.handle) {
      const who = _.get(chosen, 'influencer.name') || chosen.name;
      return (
        <Message
          warning
          style={noticeStyle}
          header="This will update the creator"
          content={`${who} has no ${field} handle. Saving will set it to @${details.handle}.`}
        />
      );
    }
    if (!selectedId && !matchedId) {
      const content = details.handle
        ? `No creator on this campaign has ${field} @${details.handle} - choose who posted it.`
        : 'This URL does not say who posted it - choose the collaboration.';
      return <Message info style={noticeStyle} content={content} />;
    }
    return null;
  }

  render() {
    const { platform, onClose } = this.props;
    const { url, details, urlError, options, selectedId, matchedId, isSearching, isSaving, error } = this.state;
    const isSuggested = Boolean(selectedId) && selectedId === matchedId;
    const chosen = _.get(_.find(options, { value: selectedId }), 'creator');

    return (
      <Modal open size="small" onClose={onClose} closeIcon>
        <Modal.Header>Add a {PLATFORM_LABEL[platform]}</Modal.Header>
        <Modal.Content>
          <div style={stepStyle}>
            <div style={stepLabelStyle}>1. Paste the post URL</div>
            <Input
              id="jumbo-content-url"
              fluid
              autoFocus
              value={url}
              onChange={this.onUrlChange}
              placeholder={platform === 'tiktok'
                ? 'https://www.tiktok.com/@handle/video/123…'
                : 'https://www.instagram.com/p/ABC123/'}
              error={Boolean(urlError)}
            />
            {urlError && <Message error size="small" style={noticeStyle} content={urlError} />}
            {details && details.handle &&
              <div style={detectedStyle}>
                Detected handle <strong>@{details.handle}</strong>
              </div>}
          </div>

          {details &&
            <div style={stepStyle}>
              <Divider />
              <div style={stepLabelStyle}>
                2. Which creator posted this?
                {isSuggested && <span style={matchBadgeStyle}>matched by handle</span>}
              </div>
              <Dropdown
                fluid
                selection
                search={keepApiOrder}
                placeholder="Search creators on this campaign…"
                options={_.map(options, o => _.omit(o, 'creator'))}
                value={selectedId}
                loading={isSearching}
                onSearchChange={this.onPickerSearch}
                onChange={this.onPickerChange}
              />
              {chosen && <CreatorCard creator={chosen} />}
              {this.renderNotice()}
            </div>}

          {error && <Message error style={noticeStyle} content={error} />}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button
            primary
            icon="plus"
            labelPosition="left"
            loading={isSaving}
            disabled={!details || !selectedId || isSaving}
            onClick={this.onConfirm}
            content={isSuggested ? 'Confirm and add' : `Add ${PLATFORM_LABEL[platform]}`}
          />
        </Modal.Actions>
      </Modal>
    );
  }
}

AddJumboContentModal.propTypes = {
  campaignId: PropTypes.string.isRequired,
  platform: PropTypes.oneOf(['tiktok', 'instagram']).isRequired,
  campaignAPIClient: PropTypes.shape({ get: PropTypes.func }).isRequired,
  onAttach: PropTypes.func.isRequired,
  onAttached: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export { handleField };
export default AddJumboContentModal;



/** WEBPACK FOOTER **
 ** ./src/containers/large-campaign/add-jumbo-content-modal.js
 **/